import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Loading, Pill, TableView, useTablePagination, Select, Button } from '@gpstrackit/gtc-react-controls';
import SeverityInfoBubble from '../../components/severityInfoBubble';
import moment from 'moment';


// Helpers
import { GetFormatForMoment , useRealtimeDrivers} from 'Core/hooks';

// Actions
import { getCaseByIdDriver } from 'Modules/driverPerformance/views/overviewTabDriver/redux/slice';
import { savePreference, sendGenericExport, setDriverPerformanceMainTab } from 'Redux/actions';

// Material UI
import { Box, Card, CardContent, List, ListItem, ListItemIcon, ListItemText, Skeleton, Typography, IconButton as MuiIconButton, Popover } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { Timezone } from 'Components/uiControls';

// Styles
import './styles.scss';
// Containers
import Preferences from 'Core/containers/Preferences';
import { Pie } from 'react-chartjs-2';
import { ArcElement, Chart as ChartJS, Legend, Tooltip, } from 'chart.js';

// Constants
const KEY_PREFERENCE = "DPDriverTab";


const KEY_PREFERENCE_CASES = "DriverPerformanceViewCases";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const CasesOverview = ({ data, nextToken, loading, dispatch, rangeDate, driverId }) => {
    const { messages, locale } = useIntl();
    const casesTableOverviewRef = useRef();
    const navigate = useNavigate();
    const { dateformat, timeZone } = useSelector((state) => state.securityRedux.user);
    const authenticatedId = useSelector(state => state.securityRedux.authenticatedId);
    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    const format = GetFormatForMoment(dateformat);
    const [page, setPage] = useState(1);

    const columns = [
        {
            key: "caseNumber",
            title: messages['driver_performance_driverDetail_overview_casesId'],
            show: true,
            width: 100,
            export: true,
            render: (row) => {
                return (
                    <div>
                        <a className="case-number-link"
                            href='#'
                            noWrap
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`/app/driverPerformance/caseDetail/${row?.id}`)
                            }}>
                            {row?.caseNumber || "N/A"}
                        </a>
                    </div>
                )
            },
            exportValue: (row) => {
                return row?.caseNumber || "N/A";
            },
            functionString: 'return row?.caseNumber || "N/A"'
        }, {
            key: 'statusId',
            title: messages['driver_performance_driverDetail_overview_status'], //name
            show: true,
            width: 100,
            export: true,
            render: (row) => {
                const item = {
                    ...row?.status,
                    label: messages[`driver_performance_cases_status_${row?.status?.label?.toLowerCase()}`] || row?.status?.label,
                    color: row?.status?.label === 'Expired' ? "black" : row?.status?.label === 'Pending' ? "grey" : row?.status?.label === 'Evaluate' ? "orange" : "#66bb6a"
                }

                return (
                    <div style={{ width: 110 }} className="casesTab-badge-status">
                        <Pill
                            key={row?.id}
                            item={item}
                            onClick={function noRefCheck() { }}
                            variant={'outlined'}
                        />
                    </div>
                )
            },
            exportValue: (row) => {
                return row?.state?.label;
            },
            functionString: 'return row?.state?.label || ""'
        }, {
            key: 'date',
            title: messages['driver_performance_driverDetail_overview_created'],
            show: true,
            width: 120,
            export: true,
            functionString: `
                function formatDate(date,tz,formatDate) {
                    let dateToFormat = moment(date)
                    let formatted = moment(dateToFormat).tz(tz).format(formatDate)
                    return formatted;
                }

                function getAbBvTimezone(timezone) {
                    let abbr = moment.tz(timezone).zoneAbbr();
                    switch (abbr) {
                        case '-05':
                            return 'COT';
                        default:
                            return abbr;
                    }
                }

                let timeZone = "${timeZone}"
                let format = "${format}" + " hh:mm a"
                let timezoneAbbr = getAbBvTimezone(timeZone)

                return row?.createdOn ? formatDate(row?.createdOn,timeZone,format) + ", " + timezoneAbbr : ''
            `,
            render: (row) => (
                row?.createdOn ? (
                    <Timezone
                        date={row.createdOn}
                        dateFormat={dateformat}
                        timeFormat="hh:mm a"
                        timezone={timeZone}
                    />
                ) : 'N/A'
            )
        }, {
            key: 'piSuccess',
            title: messages['driver_performance_driverDetail_overview_piSuccess'],
            show: true,
            width: 80,
            export: true,
            render: (row) => {
                return <div><p>{
                    row?.status?.entityId === "completed" ?
                        messages['driver_performance_driverDetail_overview_yes'] :
                        messages['driver_performance_driverDetail_overview_no']
                }</p></div>
            },
            exportValue: (row) => {
                return row?.status?.entityId === "completed" ? "Yes" : "No";
            },
            functionString: 'return row?.status?.entityId === "completed" ? "Yes" : "No"'
        }

    ]

    useEffect(() => {
        if (driverId) {
            paginationCasesOverview.refresh({
                _conditions: [
                    { fields: ['driverId'], terms: [driverId], exact: true },
                    { page: 1 }
                ]
            });
        }
    }, [driverId, dispatch]);

    useEffect(() => {
        paginationCasesOverview.setColumns(columns);
        casesTableOverviewRef?.current?.setPage(1);
        casesTableOverviewRef?.current?.setSelectedItems([]);
    }, []);

    useEffect(() => {
        paginationCasesOverview.setColumns(columns);
    }, [locale]);

    const paginationCasesOverview = useTablePagination({
        dispatch,
        action: getCaseByIdDriver,
        searchFields: ["openSearch"],
        tableRef: casesTableOverviewRef,
        sendGenericExport,
        exportName: "casesTab",
    })
    return (
        <section className="cases-overview-section">
            <div className="cases-overview-header">
                <p>{messages['driver_performance_driverDetail_overview_cases']}</p>
                <a
                    href="#"
                    style={{ color: '#005bf8' }}
                    onClick={(e) => {
                        e.preventDefault();

                        let currentDriver = drivers?.find(driver =>
                            String(driver.driverId) === String(driverId) ||
                            String(driver.id) === String(driverId)
                        );

                        let preferenceValue = {
                            drivers: currentDriver ? [currentDriver] : [],
                            timeFrame: rangeDate?.timeFrame || 'custom'
                        };

                        if (rangeDate?.startDate && rangeDate?.endDate) {
                            preferenceValue.rangeDate = {
                                start: rangeDate.startDate,
                                end: rangeDate.endDate
                            };
                        }

                        dispatch(savePreference({
                            entityName: "User",
                            entityId: authenticatedId,
                            preferenceKey: KEY_PREFERENCE_CASES,
                            preferenceValue: preferenceValue
                        })).then(() => {
                            dispatch(setDriverPerformanceMainTab(3));
                            navigate(`/app/driverPerformance/main`);
                        });

                    }}
                >{messages['driver_performance_driverDetail_overview_view']}</a>
            </div>
            <div className="cases-table-wrapper">
                <TableView
                    {...paginationCasesOverview}
                    keyColumn="caseNumber"
                    height={420}
                    topSpaceSize={400}
                    data={data || []}
                    disableItemSelection
                    onChangePage={() => {
                        if (data?.length) {
                            let newPage = page + 1
                            setPage(newPage);
                            paginationCasesOverview.refresh({
                                _conditions: [
                                    { fields: ['driverId'], terms: [driverId], exact: true },
                                    { page: newPage }
                                ]
                            });
                        }
                    }}
                    loading={loading}
                    infiniteScroll
                    nextToken={nextToken}
                    noMoreItemsMessage={messages?.['driver_performance_noMoreItems']}
                />
            </div>
        </section>
    );
};

const SummaryOverview = ({ data, loading, rangeDate, driverId }) => {
    const { messages } = useIntl();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    return (
        <section className="summary-overview-section">
            <div className="summary-overview-header">
                <p>{messages['driver_performance_driverDetail_overview_eventSummary']}</p>
            </div>
            <section>
                <div className="summary-overview-subheader">
                    <p>{messages['driver_performance_driverDetail_overview_risk']}</p>
                </div>

                <div className="summary-overview-item">
                    <p>{messages['driver_performance_driverDetail_overview_count']}</p>
                    <a
                        href="#"
                        style={{ color: '#005bf8' }}
                        onClick={(e) => {
                            e.preventDefault();

                            const params = new URLSearchParams();
                            params.append('eventsDriverId', String(driverId));
                            if (rangeDate?.timeFrame) {
                                params.append('eventsRange', rangeDate?.timeFrame);
                            } else if (rangeDate?.rangeDate) {
                                params.append('eventsStartDate', rangeDate?.rangeDate?.start);
                                params.append('eventsEndDate', rangeDate?.rangeDate?.end);
                            }
                            const queryString = params.toString();

                            if (queryString) {
                                navigate(`/app/driverPerformance/main?${queryString}`);
                            } else {
                                navigate(`/app/driverPerformance/main`);
                            }
                            dispatch(setDriverPerformanceMainTab(2));
                        }}
                    >{loading ? <Skeleton variant="text" width={50} height={20} /> : data?.items?.[0]?.riskEvents || 0}</a>
                </div>

            </section>
        </section>
    );
};

const PieRiskEventDistribution = ({ data, loading }) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const location = useLocation();
    const { id } = useParams();
    const [showSeverityInfo, setShowSeverityInfo] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
    const [anchorEl, setAnchorEl] = useState(null);
    const [filters, setFilters] = useState({
        eventTypes: [],
        severities: ['critical', 'high', 'medium', 'low']
    });
    const [appliedFilters, setAppliedFilters] = useState({
        eventTypes: [],
        severities: ['critical', 'high', 'medium', 'low']
    });

    const handleGearClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleGearClose = () => {
        setAnchorEl(null);
    };

    const handleApplyFilters = () => {
        setAppliedFilters({...filters});
        setAnchorEl(null);
    };

    const openGear = Boolean(anchorEl);

    const availableEventTypes = React.useMemo(() => {
        const events = data?.data?.items || [];
        return events.map(item => item.nameEvent).filter(Boolean);
    }, [data]);

    // Process data for chart - Same logic as PieGadget
    const processedData = React.useMemo(() => {
        try {
            const displayData = data?.data?.items || [];

            // Use data if available
            const dataToUse = (displayData && Array.isArray(displayData) && displayData.length > 0) ? displayData : [];

            if (!dataToUse || !Array.isArray(dataToUse) || dataToUse.length === 0) {
                return [];
            }

            return dataToUse.map((item) => {
                try {
                    const rawValue = item?.eventPercent || 0;
                    const numericValue = typeof rawValue === 'number' ? rawValue : parseFloat(rawValue) || 0;
                    return {
                        name: item?.nameEvent || '',
                        value: isNaN(numericValue) ? 0 : parseFloat(numericValue.toFixed(2)),
                        color: item?.color || '#cccccc',
                        severityCritical: item?.severityCritical || 0,
                        severityHigh: item?.severityHigh || 0,
                        severityMedium: item?.severityMedium || 0,
                        severityLow: item?.severityLow || 0
                    };
                } catch (error) {
                    console.error('Error processing chart item:', error);
                    return {
                        name: 'Unknown',
                        value: 0,
                        color: '#cccccc'
                    };
                }
            }).filter(item => item.value > 0); // Filter out zero values
        } catch (error) {
            console.error('Error processing chart data:', error);
            return [];
        }
    }, [data]);

    const filteredData = React.useMemo(() => {
        if (!processedData || processedData.length === 0) return [];

        return processedData.filter(item => {
            if (appliedFilters.eventTypes.length > 0 && !appliedFilters.eventTypes.includes(item.name)) {
                return false;
            }
            if (appliedFilters.severities.length > 0 && appliedFilters.severities.length < 4) {
                const hasSeverity = appliedFilters.severities.some(sev => {
                    if (sev === 'critical') return item.severityCritical > 0;
                    if (sev === 'high') return item.severityHigh > 0;
                    if (sev === 'medium') return item.severityMedium > 0;
                    if (sev === 'low') return item.severityLow > 0;
                    return false;
                });
                if (!hasSeverity) return false;
            }
            return true;
        });
    }, [processedData, appliedFilters]);

    const chartData = {
        labels: filteredData.map(item => messages[`driver_performance_${item.name}`] || item.name),
        datasets: [
            {
                data: filteredData.map(item => item.value),
                backgroundColor: filteredData.map(item => item.color),
                borderColor: filteredData.map(item => item.color),
                borderWidth: 2,
            },
        ],
    };

    const handleWedgeClick = (event, elements) => {
        if (elements.length === 0 || !filteredData) return;
        const index = elements[0].index;
        const clickedEvent = filteredData[index];
        setSelectedEvent(clickedEvent.name);
        setClickPosition({ x: event.native.clientX, y: event.native.clientY });
        setShowSeverityInfo(true);
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: filteredData.length > 0,
                callbacks: {
                    label: function (context) {
                        try {
                            const suffix = '%';
                            const value = parseFloat(context.parsed).toFixed(2);
                            const label = context?.label || 'Unknown';
                            return `${messages[`driver_performance_${label}`] || label}: ${value}${suffix}`;
                        } catch (error) {
                            console.error('Error in tooltip:', error);
                            return 'N/A';
                        }
                    }
                }
            }
        },
        cutout: 0,
        onClick: handleWedgeClick
    };

    return (
        <Card className="pie-risk-event-card">
            <CardContent>
                <div className="summary-overview-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p>{messages['risk_event_distribution']}</p>
                    <MuiIconButton size="small" onClick={handleGearClick} sx={{ padding: '4px' }}>
                        <SettingsIcon fontSize="small" />
                    </MuiIconButton>
                </div>
                <Popover
                    open={openGear}
                    anchorEl={anchorEl}
                    onClose={handleGearClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Box sx={{ p: 2, width: 320 }}>
                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>Filters</Typography>

                        <Box sx={{ mb: 2 }}>
                            <Select
                                name="eventTypes"
                                textField="label"
                                label="Event Types"
                                valueField="value"
                                placeholder="Select events"
                                isMulti={true}
                                options={availableEventTypes.map(e => ({ value: e, label: e }))}
                                isClearable={true}
                                isSearchable={true}
                                showPillsOutside={true}
                                value={filters.eventTypes.map(e => ({ value: e, label: e }))}
                                onChange={(value) => {
                                    if (!value || value.length <= 12) {
                                        setFilters({...filters, eventTypes: value ? value.map(v => v.value) : []});
                                    }
                                }}
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Select
                                name="severities"
                                textField="label"
                                label="Severity"
                                valueField="value"
                                placeholder="Select severity"
                                isMulti={true}
                                options={[
                                    { value: 'critical', label: 'Critical' },
                                    { value: 'high', label: 'High' },
                                    { value: 'medium', label: 'Medium' },
                                    { value: 'low', label: 'Low' }
                                ]}
                                isClearable={true}
                                isSearchable={false}
                                showPillsOutside={true}
                                value={filters.severities.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                                onChange={(value) => {
                                    setFilters({...filters, severities: value ? value.map(v => v.value) : []});
                                }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="primary" onClick={handleApplyFilters} style={{ fontSize: '0.875rem' }}>
                                Apply Filters
                            </Button>
                        </Box>
                    </Box>
                </Popover>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                        <Loading show />
                    </Box>
                ) : filteredData.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                        <Typography variant="body1" color="textSecondary">
                            {messages['risk_no_data'] || 'No data'}
                        </Typography>
                    </Box>
                ) : (
                    <div className="pie-chart-container">
                        {/* Pie Chart */}
                        <div className="chart-wrapper" style={{ cursor: 'pointer' }}>
                            <Pie data={chartData} options={chartOptions} />
                        </div>
                        {/* Legend */}
                        <div className="legend-wrapper">
                            <List dense>
                                {filteredData.map((item, index) => {
                                    let name = item?.name || '';
                                    return (
                                        <ListItem key={index}>
                                            <ListItemIcon>
                                                <div
                                                    className="color-indicator"
                                                    style={{ backgroundColor: item?.color }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2">
                                                        {parseFloat(item?.value).toFixed(1)}% {messages[`driver_performance_${name?.replace(/\s+/g, "")}`] || name}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    )
                                })}
                            </List>
                        </div>
                        <SeverityInfoBubble
                            open={showSeverityInfo && !!selectedEvent}
                            eventName={selectedEvent}
                            position={clickPosition}
                            data={{
                                critical: filteredData.find(d => d.name === selectedEvent)?.severityCritical || 0,
                                high: filteredData.find(d => d.name === selectedEvent)?.severityHigh || 0,
                                medium: filteredData.find(d => d.name === selectedEvent)?.severityMedium || 0,
                                low: filteredData.find(d => d.name === selectedEvent)?.severityLow || 0
                            }}
                            onClose={() => setShowSeverityInfo(false)}
                        />
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export const OverviewTabDriver = ({ driverId }) => {
    const { messages } = useIntl();
    const [rangeDate, setRangeDate] = useState({
        startDate: null,
        endDate: null,
        timeFrame: '',
    });


    const {
        data,
        loading,
        nextToken,
        riskByDriver,
        loadingRiskByDriver,
        riskEventDistribution,
        loadingRiskEventDistribution
    } = useSelector((state) => state.driverPerformanceOverviewRedux);
    const dispatch = useDispatch();

    return (
        <Preferences
            preferencesKey={KEY_PREFERENCE}
            onInitialPreferences={(data) => {
                setRangeDate({
                    startDate: data?.rangeDate?.start,
                    endDate: data?.rangeDate?.end,
                    timeFrame: data?.timeFrame,
                });
            }}
        >
            <div className="driver-detail-overview">
                <CasesOverview data={data} nextToken={nextToken} loading={loading} dispatch={dispatch} rangeDate={rangeDate} driverId={driverId} />
                <PieRiskEventDistribution data={riskEventDistribution} loading={loadingRiskEventDistribution} />
                <SummaryOverview data={riskByDriver} loading={loadingRiskByDriver} rangeDate={rangeDate} driverId={driverId} />
            </div>
        </Preferences>
    );
};

export default OverviewTabDriver;
